import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import {
  AHQueryHandlerLoading,
  AHSeparator,
  AHText,
  Box,
  useLayoutContext,
} from "arkh";
import React, { Suspense, useCallback, useLayoutEffect, useState } from "react";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { ProductExpireBatchQuery } from "./__generated__/ProductExpireBatchQuery.graphql";
import { ProductExpireBatchSetActionStatusMutation } from "./__generated__/ProductExpireBatchSetActionStatusMutation.graphql";

import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { ProductExpireBatchProductPreview } from "./ProductExpireBatchProductPreview";
import { ProductExpireBatchAction } from "./ProductExpireBatchAction";
import { ProductExpireBatchStatusFooter } from "./ProductExpireBatchStatusFooter";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { ProductExpireBatchActionList_productExpireBatch$key } from "./__generated__/ProductExpireBatchActionList_productExpireBatch.graphql";
import {
  PRODUCT_EXPIRE_BATCH_DATE_PICKER_ITEM_WIDTH,
  ProductExpireBatchDatePicker,
} from "./ProductExpireBatchDatePicker";
import {
  DEVICE_HAS_SCANNER,
  ScanToAddExpireDate,
} from "../Product/ScanToAddExpireDate";
import { DateTime } from "luxon";
import { useBarcodeScanner } from "../../common/useBarcodeScanner";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { ExpireDatePickerEdit } from "../ProductExpireBatchCreate/ExpireDatePicker";
import { ProductExpireBatch_ProductExpireBatch$key } from "./__generated__/ProductExpireBatch_ProductExpireBatch.graphql";
import { ProductExpireBatchDeleteExpireDateMutation } from "./__generated__/ProductExpireBatchDeleteExpireDateMutation.graphql";
import { useAlert } from "@/src/common/Alert";
import { useIntl } from "react-intl";

type ParamList = {
  ProductExpireBatchMain: UserLoggedInNavigatorParamList["ProductExpireBatch"];
};

const Stack = createNativeStackNavigator<ParamList>();

function InnerProductExpireBatchMainComponent(
  props: Props & { expireBatch: ProductExpireBatch_ProductExpireBatch$key }
) {
  const expireBatch = useFragment(
    graphql`
      fragment ProductExpireBatch_ProductExpireBatch on StoreProductExpireBatch {
        id
        expireAtDate {
          isoString
        }
        product @required(action: THROW) {
          id
          ...ProductExpireBatchDatePicker_StoreProduct
          ...ProductExpireBatchProductPreview_StoreProduct
        }
        ...ProductExpireBatchActionList_productExpireBatch
        ...ProductExpireBatchStatusFooter_productExpireBatch
      }
    `,
    props.expireBatch
  );
  const [deleteExpireDate] =
    useMutation<ProductExpireBatchDeleteExpireDateMutation>(graphql`
      mutation ProductExpireBatchDeleteExpireDateMutation(
        $productId: ID!
        $productExpireBatchId: ID!
      ) {
        storeProduct(id: $productId) {
          expireBatch(id: $productExpireBatchId) {
            delete
          }
        }
      }
    `);
  const [editDateOpen, setEditDateOpen] = useState(false);

  const alert = useAlert();
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const getOptions = useNavigationHeaderOptions();
  const onDeleteExpireDate = useCallback(() => {
    alert.alert(
      intl_tokens.DeleteExpireDate,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete this expire date?",
        description: "ProductExpireBatch deleteExpireDate alert message",
      }),
      [
        {
          text: intl_tokens.Delete,
          style: "destructive",
          onPress: () => {
            deleteExpireDate({
              variables: {
                productId: expireBatch.product.id,
                productExpireBatchId: expireBatch.id,
              },
              optimisticResponse: {
                storeProduct: {
                  expireBatch: {
                    __typename: "StoreProductExpireBatchMutation",
                    delete: true,
                  },
                },
              },
              onCompleted: () => {
                props.navigation.goBack();
              },
            });
          },
        },
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
      ]
    );
  }, [
    alert,
    deleteExpireDate,
    expireBatch.id,
    expireBatch.product.id,
    intl,
    intl_tokens.Cancel,
    intl_tokens.Delete,
    intl_tokens.DeleteExpireDate,
    props.navigation,
  ]);

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "CLOSE",
        headerTitle: DEVICE_HAS_SCANNER ? () => <ScanToAddExpireDate /> : "",
        onClose: () => {
          props.navigation.goBack();
        },
        more: [
          {
            label: intl_tokens.ChangeExpireDate,
            icon: "schedule",
            onPress: () => {
              setEditDateOpen(true);
            },
          },
          {
            label: intl_tokens.DeleteExpireDate,
            icon: "trash",
            color: "negative",
            onPress: () => {
              onDeleteExpireDate();
            },
          },
        ],
      })
    );
  }, [
    getOptions,
    intl_tokens.ChangeExpireDate,
    intl_tokens.DeleteExpireDate,
    props.navigation,
    onDeleteExpireDate,
  ]);
  useBarcodeScanner(
    useCallback(
      (ean) => {
        props.navigation.navigateDeprecated("Product", {
          screen: "ProductMain",
          params: {
            ean,
            type: "ean",
            state: "expireDates-add",
          },
        });
      },
      [props.navigation]
    )
  );

  return (
    <>
      <Box style={{ flex: 1 }} color="surface-primary">
        <AHSeparator />
        <ProductExpireBatchProductPreview
          onPress={() => {
            props.navigation.navigateDeprecated("Product", {
              screen: "ProductMain",
              params: {
                id: expireBatch.product.id,
                type: "id",
                state: "details",
              },
            });
          }}
          product={expireBatch.product}
        />
        <AHSeparator />
        {props.route.params.fromHistory ? (
          <Box
            style={{
              height: layout.circle.xlarge,
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <AHText
              type="subtitle"
              color="text-primary"
              spaceHorizontal="medium"
              style={{
                flexDirection: "row",
                alignItems: "center",
                width: PRODUCT_EXPIRE_BATCH_DATE_PICKER_ITEM_WIDTH,
              }}
            >
              {DateTime.fromISO(expireBatch.expireAtDate.isoString).toFormat(
                "dd-MM-yyyy"
              )}
            </AHText>
          </Box>
        ) : (
          <>
            <Suspense fallback={null}>
              <ProductExpireBatchDatePicker
                product={expireBatch.product}
                selectedId={expireBatch.id}
                setSelectedId={(id) => props.navigation.setParams({ id })}
              />
            </Suspense>
          </>
        )}
        <AHSeparator />
        <Suspense fallback={<AHQueryHandlerLoading />}>
          <ProductExpireBatchActionList batch={expireBatch} />
        </Suspense>
      </Box>
      <AHSeparator />

      <ProductExpireBatchStatusFooter batch={expireBatch} />
      <ExpireDatePickerEdit
        batchId={expireBatch.id}
        productId={expireBatch.product.id}
        onCompleted={() => {
          setEditDateOpen(false);
        }}
        onClose={() => {
          setEditDateOpen(false);
        }}
        visible={editDateOpen}
        initialDate={expireBatch.expireAtDate.isoString}
      />
    </>
  );
}

function ProductExpireBatchMainComponent(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<ProductExpireBatchQuery>(
    graphql`
      query ProductExpireBatchQuery($productExpireBatchId: ID!) {
        currentStore @required(action: THROW) {
          productExpireBatch(id: $productExpireBatchId)
            @required(action: THROW) {
            ...ProductExpireBatch_ProductExpireBatch
          }
        }
      }
    `,
    {
      productExpireBatchId: props.route.params.id,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <InnerProductExpireBatchMainComponent
      navigation={props.navigation}
      route={props.route}
      expireBatch={data.currentStore.productExpireBatch}
    />
  );
}

function ProductExpireBatchActionList(props: {
  batch: ProductExpireBatchActionList_productExpireBatch$key;
}) {
  const batch = useFragment(
    graphql`
      fragment ProductExpireBatchActionList_productExpireBatch on StoreProductExpireBatch {
        id
        activeAction {
          id
        }
        actions {
          id
          status
          ...ProductExpireBatchAction_StoreProductExpireBatchAction
        }
        product @required(action: THROW) {
          id
        }
      }
    `,
    props.batch
  );

  const [setActionStatus] =
    useMutation<ProductExpireBatchSetActionStatusMutation>(graphql`
      mutation ProductExpireBatchSetActionStatusMutation(
        $productId: ID!
        $productExpireBatchId: ID!
        $actionId: ID!
        $status: StoreProductExpireBatchActionStatus!
        $employeeId: ID
      ) @raw_response_type {
        storeProduct(id: $productId) {
          expireBatch(id: $productExpireBatchId) {
            setActionStatus(
              id: $actionId
              status: $status
              employeeId: $employeeId
            ) {
              id
              actions {
                id
                status
              }
            }
          }
        }
      }
    `);

  const { currentEmployeeId } = usePersistedEmployeeId();

  return (
    <>
      {batch.actions.map((action) => (
        <ProductExpireBatchAction
          action={action}
          isActiveAction={action.id === batch?.activeAction?.id}
          key={action.id}
          setActionStatus={(status) => {
            setActionStatus({
              optimisticResponse: {
                storeProduct: {
                  expireBatch: {
                    setActionStatus: {
                      id: batch.id,
                      actions: batch.actions.map((a) => ({
                        id: a.id,
                        status: action.id === a.id ? status : a.status,
                      })),
                    },
                  },
                },
              },
              variables: {
                productId: batch.product.id,
                productExpireBatchId: batch?.id,
                actionId: action.id,
                status,
                employeeId: currentEmployeeId,
              },
            });
          }}
        />
      ))}
    </>
  );
}

type Props = {
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"ProductExpireBatch">,
    NativeStackNavigationProp<ParamList, "ProductExpireBatchMain">
  >;
  route: RouteProp<ParamList, "ProductExpireBatchMain">;
};

function ProductExpireBatchMain(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <ProductExpireBatchMainComponent {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const ProductExpireBatch = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"ProductExpireBatch">) => {
    const getOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="ProductExpireBatchMain"
          component={ProductExpireBatchMain}
          initialParams={props.route.params}
          options={getOptions({
            type: "CLOSE",
            headerTitle: DEVICE_HAS_SCANNER
              ? () => <ScanToAddExpireDate />
              : "",
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
      </Stack.Navigator>
    );
  }
);
